import {ArrowRightOutlined, LinkOutlined} from '@ant-design/icons';
import {Alert, Card, Collapse, Flex, Table, Tag, theme, Tooltip, Typography} from 'antd';
import dayjs from 'dayjs';
import React from 'react';

import {ApiQuery} from '../../../../gen/api';
import {AdminListingAnalytics, AdminMarketComparable, AdminMarketSnapshotStatus} from '../../../../gen/api/models';
import {marketplaceLabel} from '../../../helpers/status';
import {CHECK_PROBLEM, comparableColumns, Hint, TAG_COLOR} from '../../dashboard/components/PriceAnalyticsParts';
import {
    formatAgo,
    formatPercent,
    formatPrice,
    parseMoney,
    priceChangeSuggested,
    profit,
    REASON_LABEL,
    SEVERITY_DESCRIPTION,
    SEVERITY_LABEL,
    severityTone,
} from '../../dashboard/helpers/slowSelling';

const {Text} = Typography;

/** A small grey caption above a key number. */
const Caption: React.FC<{children: React.ReactNode}> = ({children}) => (
    <Text
        type="secondary"
        style={{display: 'block', fontSize: 12, lineHeight: '16px'}}
    >
        {children}
    </Text>
);

/** One of the secondary figures under the prices: "Payout $209.75". */
const Figure: React.FC<{label: React.ReactNode; children: React.ReactNode}> = ({label, children}) => (
    <span style={{whiteSpace: 'nowrap'}}>
        <Text type="secondary">{label}</Text> {children}
    </span>
);

/**
 * One listing: the verdict, our price against the suggested one (the decision to
 * make), then payout, profit and floor, then how long it is listed.
 */
const ListingAnalytics: React.FC<{listing: AdminListingAnalytics; purchasePrice: number}> = ({
    listing: l,
    purchasePrice,
}) => {
    const {token} = theme.useToken();
    const tone = severityTone(l.severity);
    const toneColor = {
        error: token.colorError,
        warning: token.colorWarning,
        muted: token.colorBorder,
        success: token.colorSuccess,
    }[tone];

    const ourPrice = parseMoney(l.ourPrice);
    const suggestedPrice = parseMoney(l.suggestedPrice);
    const changeSuggested = priceChangeSuggested(l.ourPrice, l.suggestedPrice);
    const change = ourPrice && suggestedPrice !== undefined ? suggestedPrice / ourPrice - 1 : undefined;
    const listingProfit = profit(l.ourNet, purchasePrice);
    const suggestedProfit = profit(l.suggestedNet, purchasePrice);
    const floorAboveMarket = suggestedPrice !== undefined && (parseMoney(l.floorPrice) ?? 0) > suggestedPrice;
    const checkProblem = l.marketplaceCheck?.status ? CHECK_PROBLEM[l.marketplaceCheck.status] : undefined;
    const reasons = l.reasons.map((reason) => REASON_LABEL[reason]).filter(Boolean);

    const bigNumber: React.CSSProperties = {fontSize: 20, fontWeight: 600, lineHeight: '28px'};

    return (
        <div
            style={{
                padding: '12px 12px 10px 14px',
                borderRadius: token.borderRadiusLG,
                background: token.colorFillQuaternary,
                borderLeft: `3px solid ${toneColor}`,
            }}
        >
            <Flex
                align="center"
                gap={8}
            >
                <Text strong>{marketplaceLabel(l.marketplace)}</Text>
                <Tooltip title={SEVERITY_DESCRIPTION[l.severity]}>
                    <Tag
                        color={TAG_COLOR[tone]}
                        style={{marginInlineEnd: 0}}
                    >
                        {SEVERITY_LABEL[l.severity]}
                    </Tag>
                </Tooltip>
                {l.link ? (
                    <Tooltip title="Open listing">
                        <a
                            href={l.link}
                            target="_blank"
                            rel="noreferrer"
                            style={{marginLeft: 'auto'}}
                        >
                            <LinkOutlined />
                        </a>
                    </Tooltip>
                ) : null}
            </Flex>
            {reasons.length > 0 || checkProblem ? (
                <div style={{fontSize: 12, marginTop: 2}}>
                    {checkProblem ? <Text type="danger">{checkProblem}. </Text> : null}
                    <Text type="secondary">{reasons.join(' · ')}</Text>
                </div>
            ) : null}

            <Flex
                align="flex-start"
                gap={16}
                wrap
                style={{margin: '12px 0 10px'}}
            >
                <div>
                    <Caption>Our price</Caption>
                    <div style={bigNumber}>{formatPrice(l.ourPrice)}</div>
                    {parseMoney(l.buyerShipping) ? (
                        <Caption>+ {formatPrice(l.buyerShipping)} buyer shipping</Caption>
                    ) : null}
                </div>
                {suggestedPrice !== undefined ? (
                    <>
                        <ArrowRightOutlined style={{marginTop: 26, color: token.colorTextQuaternary}} />
                        <div>
                            <Caption>
                                <Hint
                                    label="Suggested"
                                    hint="The price that sells: 5% below the market. Red means a loss at this price."
                                />
                            </Caption>
                            <Tooltip
                                title={`At this price: payout ${formatPrice(l.suggestedNet)}, profit ${formatPrice(suggestedProfit)}`}
                            >
                                <span
                                    style={{
                                        ...bigNumber,
                                        color:
                                            (suggestedProfit ?? 0) < 0
                                                ? token.colorError
                                                : changeSuggested
                                                  ? token.colorPrimary
                                                  : undefined,
                                    }}
                                >
                                    {formatPrice(suggestedPrice)}
                                </span>
                            </Tooltip>
                            {changeSuggested && change !== undefined ? (
                                <Text
                                    type="secondary"
                                    style={{marginLeft: 6}}
                                >
                                    {formatPercent(change)}
                                </Text>
                            ) : null}
                            <Caption>
                                market {formatPrice(l.referencePrice)}
                                {l.referenceSource === 'derived_from_ebay' ? (
                                    <Tooltip title="Derived from the eBay market for this marketplace">
                                        <span> *</span>
                                    </Tooltip>
                                ) : null}
                                {l.marketChangePct !== undefined && l.marketChangePct !== null ? (
                                    <Tooltip title="How much the market moved since we last changed the price">
                                        <span> ({formatPercent(l.marketChangePct)})</span>
                                    </Tooltip>
                                ) : null}
                            </Caption>
                        </div>
                    </>
                ) : null}
            </Flex>

            <Flex
                gap="4px 16px"
                wrap
                style={{fontSize: 13}}
            >
                <Figure
                    label={
                        <Hint
                            label="Payout"
                            hint="What comes to us if it sells at our price: price + buyer shipping − marketplace fees − our shipping label."
                        />
                    }
                >
                    {formatPrice(l.ourNet)}
                </Figure>
                <Figure label="Profit">
                    <Text type={(listingProfit ?? 0) < 0 ? 'danger' : undefined}>{formatPrice(listingProfit)}</Text>
                </Figure>
                <Figure
                    label={
                        <Hint
                            label="Floor"
                            hint="The lowest price whose payout reaches our minimum: the higher of purchase price + 20% and Min payout."
                        />
                    }
                >
                    {floorAboveMarket ? (
                        <Tooltip title="Floor is above the suggested price: Min payout is more than this market gives.">
                            <Text type="warning">{formatPrice(l.floorPrice)}</Text>
                        </Tooltip>
                    ) : (
                        formatPrice(l.floorPrice)
                    )}
                </Figure>
            </Flex>

            <Text
                type="secondary"
                style={{display: 'block', fontSize: 12, marginTop: 4, opacity: 0.8}}
            >
                Listed {l.daysListed}d · price changed {l.daysSincePriceChange}d ago
                {l.marketplaceCheck?.checkedAt ? (
                    <Tooltip
                        title={`Checked on the marketplace ${dayjs(l.marketplaceCheck.checkedAt).format('MM/DD/YYYY HH:mm')}`}
                    >
                        <span>
                            {' · '}
                            {l.marketplaceCheck.condition ? `${l.marketplaceCheck.condition}, ` : ''}
                            checked {formatAgo(l.marketplaceCheck.checkedAt)}
                        </span>
                    </Tooltip>
                ) : null}
            </Text>
        </div>
    );
};

interface ProductPriceAnalyticsCardProps {
    productId: string;
}

/**
 * Price analytics of the product: the verdict for each of its listings and the
 * eBay market. Renders nothing until the worker has analyzed the product.
 */
export const ProductPriceAnalyticsCard: React.FC<ProductPriceAnalyticsCardProps> = ({productId}) => {
    const analyticsQuery = ApiQuery.useAdminProductAnalyticsQuery({id: productId}, {refetchOnWindowFocus: false});
    const analytics = analyticsQuery.data;
    const listings = analytics?.listings ?? [];
    const market = analytics?.ebayMarket;

    if (analyticsQuery.isLoading || analyticsQuery.isError || (listings.length === 0 && !market)) {
        return null;
    }

    const computedAt = listings.reduce<string | undefined>(
        (latest, l) => (!latest || dayjs(l.computedAt).isAfter(latest) ? l.computedAt : latest),
        undefined
    );
    const purchasePrice = parseMoney(analytics?.purchasePrice) ?? 0;

    return (
        <Card
            title="Price Analytics"
            size="small"
            style={{marginBottom: 16}}
            extra={
                computedAt ? (
                    <Tooltip title={dayjs(computedAt).format('MM/DD/YYYY HH:mm')}>
                        <Text
                            type="secondary"
                            style={{fontSize: 12}}
                        >
                            analyzed {formatAgo(computedAt)}
                        </Text>
                    </Tooltip>
                ) : null
            }
        >
            {listings.length > 0 && (
                <Flex
                    vertical
                    gap={8}
                    style={{marginBottom: 16}}
                >
                    {listings.map((l) => (
                        <ListingAnalytics
                            key={l.listingId}
                            listing={l}
                            purchasePrice={purchasePrice}
                        />
                    ))}
                </Flex>
            )}

            <Flex
                align="baseline"
                gap={8}
                style={{marginBottom: 4}}
            >
                <Text strong>eBay market</Text>
                {market ? (
                    <Tooltip title={dayjs(market.capturedAt).format('MM/DD/YYYY HH:mm')}>
                        <Text
                            type="secondary"
                            style={{fontSize: 12}}
                        >
                            updated {formatAgo(market.capturedAt)}
                        </Text>
                    </Tooltip>
                ) : null}
            </Flex>
            {!market ? (
                <Alert
                    type="info"
                    message="No market data yet: the worker has not searched eBay for this product."
                    showIcon
                />
            ) : market.status !== AdminMarketSnapshotStatus.MarketSnapshotStatusOk ? (
                <Alert
                    type="warning"
                    showIcon
                    message={
                        market.status === AdminMarketSnapshotStatus.MarketSnapshotStatusNoData
                            ? `Too few comparables found on eBay (${market.comparablesCount}).`
                            : `eBay search failed: ${market.error ?? market.status}`
                    }
                />
            ) : (
                <>
                    <Caption>
                        <Hint
                            label="Median"
                            hint="The median total (price + shipping) of similar eBay listings: the eBay market price."
                        />
                    </Caption>
                    <div style={{fontSize: 20, fontWeight: 600, lineHeight: '28px'}}>
                        {formatPrice(market.marketMedian)}
                    </div>
                    <Caption>
                        range {formatPrice(market.marketLow)} – {formatPrice(market.marketHigh)} ·{' '}
                        {market.comparablesCount} comparables
                    </Caption>
                    {market.comparables.length > 0 && (
                        <Collapse
                            ghost
                            size="small"
                            items={[
                                {
                                    key: 'comparables',
                                    label: <Text type="secondary">Show comparables</Text>,
                                    styles: {header: {paddingInline: 0}, body: {paddingInline: 0}},
                                    children: (
                                        <Table<AdminMarketComparable>
                                            size="small"
                                            rowKey={(c) => c.itemId}
                                            columns={comparableColumns}
                                            dataSource={market.comparables}
                                            pagination={false}
                                            scroll={{x: 'max-content', y: 320}}
                                        />
                                    ),
                                },
                            ]}
                        />
                    )}
                </>
            )}
        </Card>
    );
};
